import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Modal, Popconfirm, Space, Table } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ManageTemplateModal from './ManageTemplateModal';
import {
  RichTemplate,
  useDeleteTemplateMutation,
  useDuplicateTemplateMutation,
  useListTemplatesQuery,
} from '../../graphql/datamodel.gen';
import { ColumnsType } from 'antd/lib/table';

type Props = {
  show: boolean;
  onClose: () => void;
};

const TemplatesModal = ({ show, onClose: onCloseProp }: Props) => {
  const [deleteTemplateMut] = useDeleteTemplateMutation();
  const [duplicateTemplateMut] = useDuplicateTemplateMutation();

  const {
    data: templatesData,
    loading: isLoadingTemplates,
    refetch,
  } = useListTemplatesQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [showManageTemplateModal, setShowManageTemplateModal] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<RichTemplate | null>(
    null,
  );

  const onCloseManageTemplateModal = useCallback(() => {
    setShowManageTemplateModal(false);
    setTemplateToEdit(null);
    refetch();
  }, [refetch]);

  const onCreateTemplate = useCallback(() => {
    setShowManageTemplateModal(true);
  }, []);

  const onEditTemplate = useCallback((template: RichTemplate) => {
    setTemplateToEdit(template);
    setShowManageTemplateModal(true);
  }, []);

  const onDeleteTemplate = useCallback(
    async (template: RichTemplate) => {
      await deleteTemplateMut({
        variables: {
          id: template.id,
        },
      });
      await refetch();
    },
    [deleteTemplateMut, refetch],
  );

  const onDuplicateTemplate = useCallback(
    async (id: number) => {
      await duplicateTemplateMut({
        variables: {
          id,
        },
      });
      await refetch();
    },
    [duplicateTemplateMut, refetch],
  );

  const columns: ColumnsType<RichTemplate> = useMemo(() => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
      },
      {
        title: 'Nombre',
        dataIndex: 'name',
      },
      {
        title: '# clientes',
        render: (_, record) => record.numClients,
      },
      {
        title: 'Acciones',
        render: (_, record) => (
          <Space direction="horizontal">
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                onEditTemplate(record);
              }}
              size="small"
            >
              Editar
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                onDuplicateTemplate(record?.id);
              }}
              size="small"
            >
              Duplicar
            </Button>
            <Popconfirm
              title="¿Estás seguro de que deseas eliminar esta plantilla?"
              onConfirm={() => {
                onDeleteTemplate(record);
              }}
            >
              <Button icon={<DeleteOutlined />} danger size="small">
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];
  }, [onDeleteTemplate, onDuplicateTemplate, onEditTemplate]);

  useEffect(() => {
    if (show) {
      refetch();
    }
  }, [show]);

  const onCloseModal = useCallback(() => {
    onCloseProp();
  }, [onCloseProp]);

  return (
    <Modal
      visible={show}
      onCancel={onCloseModal}
      closable
      centered
      width={700}
      footer={null}
      destroyOnClose
      title="Plantillas de presupuesto"
    >
      <Space direction="vertical" style={{ display: 'flex' }}>
        <p>
          Gestiona las plantillas de presupuesto que se pueden asignar a
          multiples clientes. Cuando los clientes creen un presupuesto, la
          plantilla que se haya asignado se usará al generar el documento PDF
          del presupuesto.
        </p>
        <Space direction="vertical" size={'middle'} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button icon={<PlusOutlined />} onClick={onCreateTemplate}>
              Crear plantilla
            </Button>
          </div>
          <Table
            size="small"
            dataSource={templatesData?.templates || []}
            loading={isLoadingTemplates && !templatesData}
            columns={columns}
          />
        </Space>
      </Space>

      <ManageTemplateModal
        key={
          templateToEdit?.id
            ? templateToEdit.id
            : showManageTemplateModal
            ? 'true'
            : 'false'
        }
        show={showManageTemplateModal}
        template={templateToEdit}
        onClose={onCloseManageTemplateModal}
      />
    </Modal>
  );
};

export default TemplatesModal;
